import {
  AlertType,
  type DefaultPatientAlertResponse,
  type DefaultPatientEventResponse,
  type EcgRhythmSchemaResponse,
  EventCategory,
  type HfTypeSchemaResponse,
  HospitalizationEventReason,
  type NyhaClassSchemaResponse,
} from "../../../api";
import { MedicationDto } from "../../../api/api-connector";
import { compareDateStr } from "../../../utils/utils";
import { MedDataItem } from "../common/med-data";
import { DiagnosesItem } from "../diagnoses/diagnoses";

/**
 * The clinical facts the AI summary popover states beside the model's
 * reasoning.
 *
 * Every fact has two empty states and they are not interchangeable. `null`
 * means the source was read and holds nothing, which the popover states in
 * words. `undefined` means the source was not read at all — a failed request
 * or an access the caller does not have — and the popover says so instead:
 * "no hospitalizations on record" is a clinical statement, and a transient
 * 500 must never be allowed to make it.
 */
export interface SummaryFacts {
  hfType: string | null | undefined;
  nyhaClass: string | null | undefined;
  ecgRhythm: string | null | undefined;
  hospitalizations: Hospitalizations | null | undefined;
  lastCall: LastCall | null | undefined;
  /** Date the treating physician was last notified. */
  reportedOn: string | null | undefined;
  /** `null` only when the patient has no alerts at all — never when all are seen. */
  alerts: AlertsSummary | null | undefined;
  comorbidities: Comorbidity[] | null | undefined;
  medications: Medication[] | null | undefined;
}

export interface Hospitalizations {
  count: number;
  lastDate: string;
  /** Whether the most recent one was recorded as heart-failure. */
  heartFailure: boolean;
}

export interface LastCall {
  date: string;
  category: EventCategory;
}

export interface AlertsSummary {
  types: AlertType[];
  unseen: number;
}

export interface Comorbidity {
  name: string;
  inRemission: boolean;
}

export interface Medication {
  name: string;
  dose: number;
  unit: string;
}

/** Each source is `undefined` when it could not be read. */
export interface SummarySources {
  /** Latest `NYHA class` rows, read by name. */
  nyha?: MedDataItem[];
  /** Latest `ECG rhythm` rows, read by name. */
  rhythm?: MedDataItem[];
  events?: DefaultPatientEventResponse[];
  alerts?: DefaultPatientAlertResponse[];
  /** The HF type and the comorbidities, from one `hf/comorbidities` read. */
  diagnoses?: DiagnosesItem[];
  medications?: MedicationDto[];
  /** Server-side today, deciding what counts as current. */
  today: string;
}

/**
 * The two measurement types pinned by name. Both are recorded at enrolment and
 * rarely after, so the latest row of each is asked for without a time window.
 *
 * Each constant is annotated with the generated schema's literal, so a rename
 * in db-api's spec fails the build here rather than silently matching nothing.
 */
export const NYHA_MEASUREMENT: NyhaClassSchemaResponse["measurement_type"] =
  "NYHA class";

export const RHYTHM_MEASUREMENT: EcgRhythmSchemaResponse["measurement_type"] =
  "ECG rhythm";

/** The diagnosis carrying the HF type, stated as a fact of its own. */
const HF_TYPE_DIAGNOSIS: HfTypeSchemaResponse["diagnosis_name"] = "HF type";

/**
 * The label every diagnosis enum lists first: the patient was assessed for it
 * and does not have it. A recorded negative is not a comorbidity.
 */
const DIAGNOSIS_ABSENT: HfTypeSchemaResponse["diagnosis_value"] = "No";

/** The index `DIAGNOSIS_ABSENT` holds in every diagnosis enum. */
const DIAGNOSIS_ABSENT_INDEX = 0;

/**
 * db-api decodes `diagnosis_value` to its label on both read paths this file
 * draws from, so the index form is not expected to arrive. The generated type
 * permits it anyway — one annotation serves the request and the response — so
 * both forms are read as absent rather than trusting the decode.
 *
 * Diagnoses only: a measurement's first enum entry is a clinical value, not an
 * absent marker, `NYHA class` starting at `I` and `ECG rhythm` at `SR`.
 */
function isDiagnosisAbsent(value: DiagnosesItem["diagnosis_value"]): boolean {
  return value === DIAGNOSIS_ABSENT || value === DIAGNOSIS_ABSENT_INDEX;
}

const CALL_CATEGORIES: EventCategory[] = [
  EventCategory.medical_call,
  EventCategory.technical_call,
];

export function summaryFacts(sources: SummarySources): SummaryFacts {
  const { diagnoses, events, today } = sources;
  return {
    hfType: diagnoses && hfType(diagnoses),
    nyhaClass: latestMeasurementValue(sources.nyha, NYHA_MEASUREMENT),
    ecgRhythm: latestMeasurementValue(sources.rhythm, RHYTHM_MEASUREMENT),
    hospitalizations: events && hospitalizations(events),
    lastCall: events && lastCall(events),
    reportedOn: events && reportedOn(events),
    alerts: sources.alerts && alerts(sources.alerts),
    comorbidities: diagnoses && orNull(comorbidities(diagnoses, today)),
    medications:
      sources.medications && orNull(medications(sources.medications, today)),
  };
}

/**
 * A patient assessed as not having heart failure reads as unknown rather than
 * as carrying the enum's absent value for a type.
 */
function hfType(rows: DiagnosesItem[]): string | null {
  const row = rows.find((r) => r.diagnosis_name === HF_TYPE_DIAGNOSIS);
  if (row == null || isDiagnosisAbsent(row.diagnosis_value)) {
    return null;
  }
  return String(row.diagnosis_value);
}

/**
 * Filtered on the event's category, so a visit or a lab entry does not become
 * a hospitalization on the strength of its description.
 */
function hospitalizations(
  events: DefaultPatientEventResponse[],
): Hospitalizations | null {
  const rows = byCategory(events, EventCategory.hospitalization);
  const newest = latestBy(rows, (e) => e.event_date);
  if (newest == null) {
    return null;
  }
  return {
    count: rows.length,
    lastDate: newest.event_date,
    heartFailure:
      newest.event_category_details === HospitalizationEventReason.hfh,
  };
}

function lastCall(events: DefaultPatientEventResponse[]): LastCall | null {
  const newest = latestBy(
    byCategory(events, ...CALL_CATEGORIES),
    (e) => e.event_timestamp,
  );
  return newest == null
    ? null
    : { date: newest.event_date, category: newest.event_category };
}

function reportedOn(events: DefaultPatientEventResponse[]): string | null {
  const newest = latestBy(
    byCategory(events, EventCategory.physician_notified),
    (e) => e.event_date,
  );
  return newest?.event_date ?? null;
}

/** A patient whose alerts are all seen is not a patient with no alerts. */
function alerts(rows: DefaultPatientAlertResponse[]): AlertsSummary | null {
  if (rows.length === 0) {
    return null;
  }
  const types: AlertType[] = [];
  for (const row of [...rows].sort(
    (a, b) => Date.parse(b.alert_timestamp) - Date.parse(a.alert_timestamp),
  )) {
    if (!types.includes(row.alert_type)) {
      types.push(row.alert_type);
    }
  }
  return { types, unseen: rows.filter((a) => !a.alert_seen).length };
}

/**
 * A resolved diagnosis is stated rather than dropped: a reader given a short
 * list cannot tell a missing entry from an absent one. A diagnosis recorded as
 * absent is dropped, and the HF type is stated as its own fact.
 */
function comorbidities(rows: DiagnosesItem[], today: string): Comorbidity[] {
  const entries = new Map<string, Comorbidity>();
  for (const row of rows) {
    const name = row.diagnosis_name.trim();
    const key = name.toLowerCase();
    if (
      !name ||
      name === HF_TYPE_DIAGNOSIS ||
      isDiagnosisAbsent(row.diagnosis_value) ||
      entries.has(key)
    ) {
      continue;
    }
    entries.set(key, { name, inRemission: onOrBefore(row.remission, today) });
  }
  return [...entries.values()];
}

/** The same medication recorded twice is listed once — seen on real records. */
function medications(rows: MedicationDto[], today: string): Medication[] {
  const entries = new Map<string, Medication>();
  for (const row of rows) {
    const name = row.medication_name.trim();
    if (!name || before(row.medication_ended, today)) {
      continue;
    }
    const key = [
      name.toLowerCase(),
      row.medication_dose,
      row.medication_unit.toLowerCase(),
    ].join("|");
    if (!entries.has(key)) {
      entries.set(key, {
        name,
        dose: row.medication_dose,
        unit: row.medication_unit,
      });
    }
  }
  return [...entries.values()];
}

function byCategory(
  events: DefaultPatientEventResponse[],
  ...categories: EventCategory[]
): DefaultPatientEventResponse[] {
  return events.filter((e) => categories.includes(e.event_category));
}

/** An empty list is a source that was read and holds nothing. */
function orNull<T>(rows: T[]): T[] | null {
  return rows.length > 0 ? rows : null;
}

/**
 * Recorded dates are compared as calendar days, so a stored timestamp and a
 * stored date answer alike and no timezone enters into it.
 */
function onOrBefore(value: string | null | undefined, today: string): boolean {
  return !!value && compareDateStr(value.slice(0, 10), today) <= 0;
}

/** Whether a recorded date is already past — an end date that has run out. */
function before(value: string | null | undefined, today: string): boolean {
  return !!value && compareDateStr(value.slice(0, 10), today) < 0;
}

/**
 * The most recent value of one measurement type, `null` when the patient has
 * none and `undefined` when the rows were not read.
 */
function latestMeasurementValue(
  rows: MedDataItem[] | undefined,
  type: string,
): string | null | undefined {
  if (rows == null) {
    return undefined;
  }
  const row = latestBy(
    rows.filter((r) => r.measurement_type === type),
    (r) => r.measurement_datetime,
  );
  return row == null ? null : String(row.measurement_value);
}

/**
 * The row with the newest date. db-api's list endpoints do not promise an
 * order, so recency is decided here rather than assumed from the response. A
 * row whose date will not parse never wins, but is returned when no row in the
 * list parses at all.
 */
function latestBy<T>(rows: T[], date: (row: T) => string): T | undefined {
  let best: T | undefined;
  let bestTime = -Infinity;
  for (const row of rows) {
    const time = Date.parse(date(row));
    if (!Number.isNaN(time) && time > bestTime) {
      bestTime = time;
      best = row;
    }
  }
  return best ?? rows[0];
}
